import {
  ListWorkflowsPaginatedError,
  ListWorkflowsResponse,
  Schemas,
  fetchListWorkflowsPaginated,
} from '@automata/wfb-api';
import { useEffect, useState } from 'react';

export const useListWorkflowsSequential = (params: {
  queryParams: { workflow_type: Schemas.WorkflowType };
  enabled?: boolean;
}) => {
  const { enabled = true } = params;
  const [allData, setAllData] = useState<ListWorkflowsResponse | null>(null);
  const [error, setError] = useState<ListWorkflowsPaginatedError | null>(null);
  const [lastKey, setLastKey] = useState<Record<string, any> | null>(null);
  const [shouldFetch, setShouldFetch] = useState(true);

  useEffect(() => {
    if (!shouldFetch || !enabled) return;

    let cancelled = false;

    const fetchPage = async () => {
      try {
        const response = await fetchListWorkflowsPaginated({
          queryParams: {
            workflow_type: params.queryParams.workflow_type,
          },
          body: lastKey ? { last_evaluated_key: lastKey } : null,
        });

        if (cancelled) return;

        setAllData((prev) => [...(prev ?? []), ...response.workflows]);

        if (
          response.pagination.has_more &&
          response.pagination.last_evaluated_key
        ) {
          setLastKey(response.pagination.last_evaluated_key);
          setShouldFetch(true);
        } else {
          setShouldFetch(false);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err as ListWorkflowsPaginatedError);
        setShouldFetch(false);
      }
    };

    fetchPage();

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, lastKey, params.queryParams.workflow_type, enabled]);

  return {
    data: allData,
    isError: error !== null,
    error,
    isLoading: allData === null && error === null && enabled,
    fetchStatus: (enabled && shouldFetch ? 'fetching' : 'idle') as
      | 'fetching'
      | 'paused'
      | 'idle',
  };
};
